import { useEffect, useState } from 'react';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { getTenantLoginContext } from '@/utils/subdomain';
import {
  applyPwaBranding,
  brandFromCompany,
  getEffectivePwaBrand,
  type PwaBrand,
} from '@/utils/pwaBranding';

/**
 * Boots PWA name/icons early:
 * 1) session cache (tenant from prior visit)
 * 2) tenant subdomain → check_subdomain (pre-login)
 * 3) authenticated → company profile name + logo
 */
export function usePwaBrandBootstrap(): PwaBrand {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [brand, setBrand] = useState<PwaBrand>(() => getEffectivePwaBrand());

  // Immediate: cached / platform default so Chrome sees a manifest ASAP
  useEffect(() => {
    void applyPwaBranding(getEffectivePwaBrand()).then(setBrand);
  }, []);

  // Tenant subdomain before login (e.g. sample.getkashly.com)
  useEffect(() => {
    if (isAuthenticated) return;
    const { isTenantLogin, subdomain } = getTenantLoginContext();
    if (!isTenantLogin || !subdomain) return;

    void authApi
      .checkSubdomain(subdomain)
      .then((res) => {
        const company = res.data?.message;
        if (!company?.company_name && !company?.company_logo) return;
        const next = brandFromCompany(company);
        return applyPwaBranding(next).then(setBrand);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  // Logged-in: always prefer company profile
  useEffect(() => {
    if (!isAuthenticated) return;
    void authApi
      .getCompanyProfile()
      .then((res) => {
        const profile = res.data?.message;
        if (!profile) return;
        const next = brandFromCompany(profile);
        return applyPwaBranding(next).then(setBrand);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  return brand;
}
